import { DOSSIERS_QUERY_KEY, invalidateOpenDossiersQueries, useUpdateDossierPropertyQueriesData } from "@/entities/dossier";
import { useApiClient } from "@/infrastructure/rest";
import { OpstartplanEvent, useRealtimeTopic } from "@/infrastructure/rsocket";
import { OpstartplanDto, PutOpstartplanRequestDto } from "@/shared/types";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { OPSTARTPLAN_QUERY_KEY } from "./constants";
import { Opstartplan } from "./domain";

export function useGetDossierOpstartplanById(id: string) {
    const apiClient = useApiClient();

    return useQuery({
        queryKey: [DOSSIERS_QUERY_KEY, id, OPSTARTPLAN_QUERY_KEY],
        queryFn: async () => {
            const data = await apiClient.get<OpstartplanDto>(`/dossiers/${id}/opstartplan`);
            return data ? Opstartplan.fromDto(data) : null;
        },
    });
}

type PutOpstartplan = PutOpstartplanRequestDto & {
    dossierId: string;
};

export function usePutOpstartplan() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId, ...data }: PutOpstartplan) => {
            await apiClient.put(`/dossiers/${dossierId}/opstartplan`, data);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error putting opstartplan", error);
        },
    });
}

export type DeleteOpstartplan = {
    dossierId: string;
};

export function useDeleteOpstartplan() {
    const apiClient = useApiClient();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ dossierId }: DeleteOpstartplan) => {
            await apiClient.delete(`/dossiers/${dossierId}/opstartplan`);
        },
        onSuccess: async (_, variables) => {
            await invalidateOpenDossiersQueries(queryClient, variables.dossierId);
        },
        onError: (error) => {
            console.error("Error deleting opstartplan", error);
        },
    });
}

export function useRealtimeOpstartplan() {
    const { handleEvent } = useUpdateDossierPropertyQueriesData("opstartplan");

    useRealtimeTopic<OpstartplanEvent>("opstartplan", {
        onMessage: (message) => {
            const { opstartplan, dossierId, reason } = message;
            handleEvent(dossierId, opstartplan.id, reason, Opstartplan.fromDto(opstartplan));
        },
    });
}
